import { useEffect, useState } from 'react';
import {
  User as UserIcon,
  FileText as FileIcon,
  Database,
  TrendingUp,
  Settings as SettingsIcon,
  Upload,
  Clock,
  RussianRuble,
  FolderOpen as FolderIcon,
  Menu,
  User,
  LogOut,
  X,
  PlusCircleIcon
} from 'lucide-react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import DocumentUpload from './DocumentUpload';
import { MyDocuments } from './MyDocuments';
import { DocumentRegistry } from './DocumentRegistry';
import { MyStatistic } from './MyStatistic';
import { UserProfile } from './UserProfile';
import { useOutletContext } from 'react-router-dom';
import BindingCards from './BindingCards';
import BecomeSeller from './BecomeSeller';
import { authFetch } from '../utils/authFetch';
import { API_URL } from '../config';


export function UserAccountLayout() {
  const { onLogout, userData, onUpdateUserData } = useOutletContext();
  const [activeTab, setActiveTab] = useState('registry');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // Track user's document status
  const [hasUploadedDocuments, setHasUploadedDocuments] = useState(true) // Set to false for new users
  const [documentsInModeration, setDocumentsInModeration] = useState(1) // Number of docs in moderation

  // Profile edit state
  const [profileData, setProfileData] = useState(userData);

  const [isSeller, setIsSeller] = useState(false);

  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('bindingResultSub') === 'success') {
      setActiveTab('become-seller');
    }
  }, [location.search]);

  useEffect(() => {
    const isAccountPage = location.pathname.endsWith('/account');
    const needPolling = localStorage.getItem('payoutBindInProgress') === '1';

    console.log('isAccountPage: ', isAccountPage);
    console.log('needPolling: ', needPolling);

    if (isAccountPage && needPolling) {
      setActiveTab('profile');
    }
  }, [location.pathname]);


  const handleIsSeller = async () => {
      const jwt = localStorage.getItem("token");
      if (!jwt) {
        alert("Пожалуйста, войдите");
        return;
      }
  
      try {
        const res = await authFetch(`${API_URL}/about-user`, {
          method: "GET",
        });
  
        if (res.status === 401) {
          alert("Сессия истекла. Войдите заново.");
          return;
        }
        if (!res.ok) {
          const detail = await res.text().catch(() => "");
          alert(`Не удалось загрузить профиль. ${detail || ""}`);
          return;
        }

        const data = await res.json();
  
        if (data.has_card === true && data.is_active_subscription === true) {
          setIsSeller(true);
        }
        else {
          setIsSeller(false);
        }
  
      } catch (e) {
        console.error(e);
        alert("Произошла ошибка при отмене подписки.");
      }
    }


  // Function to get header status
  const getHeaderStatus = () => {
    if (!hasUploadedDocuments) {
      return {
        text: 'Загрузить документ',
        icon: Upload,
        variant: 'outline',
        className: 'text-[#2563eb] border-[#2563eb] hover:bg-blue-50 cursor-pointer',
        onClick: () => setActiveTab('documents')
      }
    } else if (documentsInModeration > 0) {
      return {
        text: `${documentsInModeration} на модерации`,
        icon: Clock,
        variant: 'outline',
        className: 'text-yellow-600 border-yellow-200'
      }
    } else {
      return {
        text: 'Все документы одобрены',
        icon: RussianRuble,
        variant: 'outline',
        className: 'text-green-600 border-green-200'
      }
    }
  }


  const handleMenuItemClick = (itemId) => {
    setActiveTab(itemId)
    setIsMobileMenuOpen(false)
  }


  const renderContent = () => {
    switch (activeTab) {
      case 'registry':
        return <DocumentRegistry userData={userData} />;
      case 'documents':
        return <DocumentUpload onLogout={onLogout} />;
      case 'my-documents':
        return <MyDocuments />;
      case 'earnings':
        return <MyStatistic />;
      case 'profile':
        return <UserProfile userData={userData} onSellerUpdate={setIsSeller} />;
      case 'notifications':
        return (
          <div className="p-6">Здесь уведомления</div>
        );
      case 'settings':
        return (
          <div className="p-6">Здесь системные настройки</div>
        );
      case 'security':
        return <BindingCards />;
      case 'become-seller':
        return <BecomeSeller userData={userData} onSellerChange={setIsSeller} />;
      default:
        return null;
    }
  };

  useEffect(() => {
    handleIsSeller();
  }, [])

  const menuItemsBase = [
    { id: 'registry', label: 'Реестр документов', icon: Database },
    { id: 'profile', label: 'Профиль', icon: UserIcon },
    { id: 'my-documents', label: 'Мои документы', icon: FolderIcon },
    // { id: 'security', label: 'Безопасность',       icon: SettingsIcon },
    { id: 'earnings', label: 'Аналитика', icon: TrendingUp },
    { id: 'become-seller', label: 'Стать продавцом',       icon: PlusCircleIcon },
  ];

  const menuItemsSeller = [
    { id: 'registry', label: 'Реестр документов', icon: Database },
    { id: 'profile', label: 'Профиль', icon: UserIcon },
    { id: 'my-documents', label: 'Мои документы', icon: FolderIcon },
    // { id: 'security', label: 'Безопасность',       icon: SettingsIcon },
    { id: 'documents', label: 'Загрузка', icon: Upload },
    { id: 'earnings', label: 'Аналитика', icon: TrendingUp },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="px-3 sm:px-4 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <div className="flex items-center gap-2 sm:gap-4">
              {/* Mobile menu button */}
              <Button
                variant="ghost"
                size="sm"
                className="lg:hidden p-2"
                onClick={() => setIsMobileMenuOpen(true)}
              >
                <Menu className="h-5 w-5" />
              </Button>
              <h1 className="text-lg sm:text-xl font-semibold text-gray-900">Личный кабинет</h1>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="hidden sm:flex items-center gap-2 text-gray-600">
                <User className="h-4 w-4" />
                <span className="text-sm">{userData?.name || 'Пользователь'}</span>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  console.log('native button clicked!');
                  onLogout();
                }}
                className="text-red-600 border-red-200 hover:bg-red-50 text-xs sm:text-sm p-2 sm:px-3"
              >
                <LogOut className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
                <span className="hidden sm:inline">Выйти</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile menu overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black/20" onClick={() => setIsMobileMenuOpen(false)} />
          <div className="fixed left-0 top-0 h-full w-80 bg-white shadow-xl">
            <div className="p-4 border-b">
              <div className="flex items-center justify-between">
                <h2 className="font-semibold">Меню</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>
            <nav className="p-2 flex flex-col">
              {isSeller === true ? (
                menuItemsSeller.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleMenuItemClick(item.id)}
                  className={`
                    w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left transition-colors relative
                    ${activeTab === item.id 
                      ? 'bg-blue-50 text-[#2563eb] border border-blue-200' 
                      : 'text-gray-600 hover:bg-gray-50'
                    }
                  `}
                >
                  <item.icon className="h-5 w-5" />
                  <span className="font-medium">{item.label}</span>
                </button>
              ))
              ) : (
                menuItemsBase.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleMenuItemClick(item.id)}
                  className={`
                    w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left transition-colors relative
                    ${
                      item.id === 'become-seller' ? 'gradient-border mt-auto' : ''
                    }
                    ${activeTab === item.id 
                      ? 'bg-blue-50 text-[#2563eb] border border-blue-200' 
                      : 'text-gray-600 hover:bg-gray-50'
                    }
                  `}
                >
                  <item.icon className="h-5 w-5" />
                  <span className="font-medium">{item.label}</span>
                </button>
              ))
              )}
            </nav>
          </div>
        </div>
      )}

      <div className="flex">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:block w-64 flex-shrink-0 bg-white border-r border-gray-200 min-h-screen">
          <div className="sticky top-20">
            <nav className="p-2">
              {isSeller === true ? (
                menuItemsSeller.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`
                    w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors relative
                    ${activeTab === item.id 
                      ? 'bg-blue-50 text-[#2563eb] border border-blue-200' 
                      : 'text-gray-600 hover:bg-gray-50'
                    }
                  `}
                >
                  <item.icon className="h-5 w-5" />
                  <span className="font-medium">{item.label}</span>
                </button>
              ))
              ) : (
                menuItemsBase.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    className={`
                      w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors relative
                      ${
                        item.id === 'become-seller' ? 'gradient-border mt-auto' : ''
                      }
                      ${activeTab === item.id 
                        ? 'bg-blue-50 text-[#2563eb] border border-blue-200' 
                        : 'text-gray-600 hover:bg-gray-50'
                      }
                    `}
                  >
                    <item.icon className="h-5 w-5" />
                    <span className="font-medium">{item.label}</span>
                  </button>
                ))
              )}
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-w-0">
          <div className="bg-white shadow-sm border-b border-gray-200 lg:border-0 min-h-screen">
            {renderContent()}
          </div>
        </main>
      </div>
    </div>
  );
}
